import { useState, useEffect } from 'react'
import './styles/common.css'
import './styles/govDesignSystem.css'
import { UserManagementPage } from './pages/admin/UserManagementPage'
import { UserDetailPage } from './pages/admin/UserDetailPage'
import { BindingsListPage } from './pages/admin/bindings/BindingsListPage'
import { BindingDetailPage } from './pages/admin/bindings/BindingDetailPage'
import { ProcedureTypesPage } from './pages/admin/ProcedureTypesPage'
import { ProcedureTypeDetailPage } from './pages/admin/ProcedureTypeDetailPage'
import { FormVersionList } from './pages/admin/forms/FormVersionList'
import { FormVersionDetail } from './pages/admin/forms/FormVersionDetail'
import { WorkflowsListPage } from './pages/admin/WorkflowsListPage'
import { WorkflowDetailPage } from './pages/admin/WorkflowDetailPage'
import { ApprovalRoutesListPage } from './pages/admin/ApprovalRoutesListPage'
import { ApprovalRouteDetailPage } from './pages/admin/ApprovalRouteDetailPage'
import { DemoLoginPage } from './pages/auth/DemoLoginPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { AdminLayout } from './components/layout/AdminLayout'

type Page = 'login' | 'forgot-password' | 'users' | 'user-detail' | 'user-management' | 'procedure-types' | 'procedure-type-detail' | 'bindings-list' | 'binding-new' | 'binding-detail' | 'form-version-list' | 'form-version-detail' | 'workflows-list' | 'workflow-detail' | 'approval-routes-list' | 'approval-route-detail'

interface User {
  id: string;
  name: string;
  role: 'APPLICANT' | 'APPROVER' | 'ADMIN';
  email: string;
}

function App() {
  const [currentPage, setCurrentPage] = useState<Page>('login')
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [pageData, setPageData] = useState<any>(null)

  // Restore user from localStorage on mount
  useEffect(() => {
    const storedUserStr = localStorage.getItem('currentUser')
    if (storedUserStr) {
      try {
        const storedUser = JSON.parse(storedUserStr)
        setCurrentUser(storedUser)
        setCurrentPage('bindings-list')
      } catch (error) {
        console.error('Error parsing stored user:', error)
        localStorage.removeItem('currentUser')
      }
    }
  }, [])

  // ログイン機能
  const login = (user: User) => {
    setCurrentUser(user)
    localStorage.setItem('currentUser', JSON.stringify(user))
    setCurrentPage('bindings-list')
  }

  // ログアウト機能
  const logout = () => {
    setCurrentUser(null)
    localStorage.removeItem('currentUser')
    setCurrentPage('login')
  }

  // ページナビゲーション
  const handleNavigate = (page: string, data?: any) => {
    setCurrentPage(page as Page)
    setPageData(data || null)
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'users':
        return <UserManagementPage onNavigate={handleNavigate} />
      case 'user-detail':
        return <UserDetailPage userId={pageData?.userId} onNavigate={handleNavigate} />
      case 'user-management':
        return <UserManagementPage onNavigate={handleNavigate} />
      case 'procedure-types':
        return <ProcedureTypesPage onNavigate={handleNavigate} />
      case 'procedure-type-detail':
        return <ProcedureTypeDetailPage procedureTypeCode={pageData?.procedureTypeCode} onNavigate={handleNavigate} />
      case 'form-version-list':
        return <FormVersionList onNavigate={handleNavigate} />
      case 'form-version-detail':
        return <FormVersionDetail formVersionId={pageData?.formVersionId} onNavigate={handleNavigate} />
      case 'workflows-list':
        return <WorkflowsListPage onNavigate={handleNavigate} />
      case 'workflow-detail':
        return <WorkflowDetailPage workflowVersionId={pageData?.workflowVersionId} onNavigate={handleNavigate} />
      case 'approval-routes-list':
        return <ApprovalRoutesListPage onNavigate={handleNavigate} />
      case 'approval-route-detail':
        return <ApprovalRouteDetailPage approvalRouteVersionId={pageData?.approvalRouteVersionId} onNavigate={handleNavigate} />
      case 'bindings-list':
        return <BindingsListPage onNavigate={handleNavigate} />
      case 'binding-new':
        return <BindingDetailPage onNavigate={handleNavigate} />
      case 'binding-detail':
        return <BindingDetailPage bindingId={pageData?.bindingId} onNavigate={handleNavigate} />
      case 'login':
        return <DemoLoginPage onLogin={login} onForgotPassword={() => setCurrentPage('forgot-password')} />
      case 'forgot-password':
        return <ForgotPasswordPage onBackToLogin={() => setCurrentPage('login')} />
      default:
        return (
          <div>Unknown Page</div>
        )
    }
  }

  // ログイン状態チェック
  if (!currentUser) {
    return renderPage()
  }

  // 管理者権限チェック
  if (currentUser.role !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">アクセス権限がありません</h2>
          <p className="text-gray-600 mb-6">管理画面は管理者のみアクセス可能です。</p>
          <button onClick={logout} className="btn btn-secondary">
            ログイン画面に戻る
          </button>
        </div>
      </div>
    )
  }

  return (
    <AdminLayout
      currentPage={currentPage}
      currentUser={currentUser}
      onNavigate={(page) => setCurrentPage(page as Page)}
      onLogout={logout}
    >
      {renderPage()}
    </AdminLayout>
  )
}

export default App